import _ from "lodash";
import convert from 'convert-length';
import {getNextItemKey} from "../../common/utils/item-key-util";
import {DEFAULT_SETTINGS, DESIGN_KEY_PREFIX} from "../constants/design.constant";
import {COLOR_LIST} from "../../common/constants/color.constant";

export {
    processDesignVariantDetail,
    processDisplayTextDetail,
    prepareImageFileName,
    prepareDesignVariantLabel,
    processTextItemsLayer,
    processImagesLayer,
    setSettingsToTextItems,
    processApplyTemplateStyle,
    extractColorsFromDesignVariant,
    prepareSelectedSize
}

const processDesignVariantDetail = (designVariantDetail, designDetail, quoteDetail, parentProduct) => {
    processSettings(designVariantDetail, parentProduct, quoteDetail, designDetail);
    prepareImageFileName(designVariantDetail, designDetail);
}

const prepareImageFileName = (designVariantDetail, designDetail) => {
    prepareDesignVariantLabel(designVariantDetail, designDetail);
    let imageFileName = designVariantDetail.name + '-' + designDetail?.podName + '-' + designVariantDetail.variantIndex;
    designVariantDetail.imageFileName = _.kebabCase(imageFileName);
}

const processSettings = (designVariantDetail, parentProduct, quoteDetail, designDetail) => {
    const settings = designVariantDetail?.settings || DEFAULT_SETTINGS;
    settings.displayText = settings.displayText || quoteDetail.displayText;

    if (!settings.background) {
        settings.background = {colorType: 'solid'};
    }

    let selectedSize = prepareSelectedSize(designVariantDetail, parentProduct, designDetail);

    if (settings.halfWidthDesign) {
        settings.width = _.parseInt(String(selectedSize.widthPixel / 2));
        settings.height = selectedSize.heightPixel;
    } else {
        settings.width = selectedSize.widthPixel;
        settings.height = selectedSize.heightPixel;
    }

    if (settings.x !== 0 || settings.y !== 0) {
        settings.x = 0;
        settings.y = 0;
    }

    processDisplayTextDetail(settings);

    designVariantDetail.settings = settings;
}

const prepareSelectedSize = (designVariantDetail, parentProduct, designDetail) => {
    const settings = designVariantDetail?.settings || DEFAULT_SETTINGS;
    let selectedSize = {
        widthPixel: parentProduct.widthPixel || settings.width || DEFAULT_SETTINGS.width,
        heightPixel: parentProduct.heightPixel || settings.height || DEFAULT_SETTINGS.height
    };

    if (parentProduct?.isMultiMeasurement && designDetail?.size) {
        const sizeMap = _.keyBy(parentProduct?.sizes, 'name');
        selectedSize = sizeMap[designDetail?.size];
    }

    // console.log({selectedSize})

    return selectedSize;
}

const processDisplayTextDetail = (settings) => {
    const displayTextItems = _.split(settings.displayText, '\n');
    const tempDisplayTextItems = [];

    _.forEach(displayTextItems, (text, index) => {
        text = text.trim();

        if (!_.isEmpty(text)) {
            let newTextItem = {
                text: text,
                key: getNextItemKey(tempDisplayTextItems, DESIGN_KEY_PREFIX.TEXT),
            };

            tempDisplayTextItems.push(newTextItem);
        }

    });

    settings.textItems = tempDisplayTextItems;
}

const processTextItemsLayer = (designVariant) => {
    const textItems = designVariant.settings.textItems;
    const existingTextItems = designVariant.textItems || [];
    const existingTextItemsMap = _.keyBy(existingTextItems, 'key');
    const newTextItems = [];
    let existingTextItem;

    _.forEach(textItems, singleTextItem => {
        existingTextItem = existingTextItemsMap[singleTextItem.key];

        if (existingTextItem) {
            existingTextItem.text = singleTextItem.text;
        } else {
            existingTextItem = singleTextItem;
        }

        applyAllSettingsAttributesToTextItems(designVariant.settings, existingTextItem);
        newTextItems.push(existingTextItem);
    });

    designVariant.textItems = newTextItems;
}

const processImagesLayer = (designVariant) => {
    const existingImages = designVariant.images || [];

    _.forEach(existingImages, existingImage => {
        if (_.isEmpty(existingImage?.key)) {
            existingImage.key = getNextItemKey(existingImages, DESIGN_KEY_PREFIX.IMAGE);
        }
    });

    designVariant.images = existingImages;
}

const applyAllSettingsAttributesToTextItems = (settings, textItem) => {
    textItem.fontSize = textItem.fontSize || settings.fontSize || 200;
    textItem.fontFamily = textItem.fontFamily || settings.fontFamily || "Aiushtya";

    applyAttribute(settings, textItem, 'colorKey');
    applyAttribute(settings, textItem, 'colorCodeKey');
    applyAttribute(settings, textItem, 'hexCode');
    applyAttribute(settings, textItem, 'fill');

    applyAttribute(settings, textItem, 'strokeColorKey');
    applyAttribute(settings, textItem, 'strokeColorCodeKey');
    applyAttribute(settings, textItem, 'strokeHexCode');
    applyAttribute(settings, textItem, 'stroke');
    applyAttribute(settings, textItem, 'strokeWidth', 'number');

    applyAttribute(settings, textItem, 'shadowColorKey');
    applyAttribute(settings, textItem, 'shadowColorCodeKey');
    applyAttribute(settings, textItem, 'shadowColor');
    applyAttribute(settings, textItem, 'shadowBlur', 'number');
    applyAttribute(settings, textItem, 'shadowOffsetX', 'number');
    applyAttribute(settings, textItem, 'shadowOffsetY', 'number');

    applyAttribute(settings, textItem, 'rotation', 'number');

    applyAttribute(settings, textItem, 'fillAfterStrokeEnabled');
    applyAttribute(settings, textItem, 'lineJoin');
}

const applyAttribute = (settings, textItem, attribute, attributeType?) => {
    textItem[attribute] = textItem[attribute] || settings[attribute];

    if (attributeType === 'number') {
        textItem[attribute] = _.parseInt(textItem[attribute]);

        if (_.isNaN(textItem[attribute])) {
            delete textItem[attribute];
        }
    }

    if (attributeType === 'float') {
        if (!_.isEmpty(textItem[attribute])) {
            textItem[attribute] = parseFloat(textItem[attribute]);
        } else {
            textItem[attribute] = 0;
        }
    }
}

const setSettingsToTextItems = (designVariant, attributeName, attributeType) => {
    _.forEach(designVariant.textItems, singleTextItem => {
        if (attributeType === 'number') {
            singleTextItem[attributeName] = _.parseInt(designVariant.settings[attributeName]);
        } else {
            singleTextItem[attributeName] = designVariant.settings[attributeName];
        }

        if (attributeName === 'colorKey' || attributeName === 'colorCodeKey') {
            singleTextItem.hexCode = designVariant.settings.hexCode;
            singleTextItem.fill = designVariant.settings.fill;
        }

        if (attributeName === 'strokeColorKey' || attributeName === 'strokeColorCodeKey') {
            singleTextItem.strokeHexCode = designVariant.settings.strokeHexCode;
            singleTextItem.stroke = designVariant.settings.stroke;
        }

        if (attributeName === 'shadowColorKey' || attributeName === 'shadowColorCodeKey') {
            singleTextItem.shadowColor = designVariant.settings.shadowColor;
        }

        singleTextItem.fillAfterStrokeEnabled = true;
        singleTextItem.lineJoin = 'round';
    });
}

const processApplyTemplateStyle = (designVariantDetail, quoteTemplate) => {
    const settings = _.cloneDeep(designVariantDetail.settings);

    const ignoreAttributes = [
        'height',
        'width',
        'textItems',
        'displayText',
    ];

    _.forIn(quoteTemplate?.settings, (value, attribute) => {
        if (ignoreAttributes.indexOf(attribute) === -1) {
            settings[attribute] = value;
        }
    });

    designVariantDetail.settings = settings;

    if (!_.isEmpty(quoteTemplate.shapes)) {
        designVariantDetail.shapes = quoteTemplate.shapes;
    }

    if (!_.isEmpty(quoteTemplate.images)) {
        designVariantDetail.images = quoteTemplate.images;
    }

    applyAllSettingsAttributesToTextItemsList(designVariantDetail, quoteTemplate);
}

const applyAllSettingsAttributesToTextItemsList = (designVariantDetail, quoteTemplate) => {
    let settings = designVariantDetail.settings;
    const textItems = designVariantDetail.textItems;
    const quoteTemplateTextItems = quoteTemplate.textItems;

    _.forEach(textItems, (textItem, index) => {
        if (quoteTemplateTextItems.length > 0) {
            if (_.isEmpty(quoteTemplateTextItems[index])) {
                settings = designVariantDetail.settings;
            } else {
                settings = quoteTemplateTextItems[index];
            }
        }

        applyAttributeFromSettings(settings, textItem, 'fontSize', 'number');
        applyAttributeFromSettings(settings, textItem, 'fontFamily');

        applyAttributeFromSettings(settings, textItem, 'colorKey');
        applyAttributeFromSettings(settings, textItem, 'colorCodeKey');
        applyAttributeFromSettings(settings, textItem, 'hexCode');
        applyAttributeFromSettings(settings, textItem, 'fill');

        applyAttributeFromSettings(settings, textItem, 'strokeColorKey');
        applyAttributeFromSettings(settings, textItem, 'strokeColorCodeKey');
        applyAttributeFromSettings(settings, textItem, 'strokeHexCode');
        applyAttributeFromSettings(settings, textItem, 'stroke');
        applyAttributeFromSettings(settings, textItem, 'strokeWidth', 'number');

        applyAttributeFromSettings(settings, textItem, 'shadowColorKey');
        applyAttributeFromSettings(settings, textItem, 'shadowColorCodeKey');
        applyAttributeFromSettings(settings, textItem, 'shadowColor');
        applyAttributeFromSettings(settings, textItem, 'shadowBlur', 'number');
        applyAttributeFromSettings(settings, textItem, 'shadowOffsetX', 'number');
        applyAttributeFromSettings(settings, textItem, 'shadowOffsetY', 'number');

        applyAttributeFromSettings(settings, textItem, 'fillAfterStrokeEnabled');
        applyAttributeFromSettings(settings, textItem, 'lineJoin');

        applyAttributeFromSettings(settings, textItem, 'radius', 'number');
        applyAttributeFromSettings(settings, textItem, 'letterSpacing', 'number');
        applyAttributeFromSettings(settings, textItem, 'startAngle', 'number');
        applyAttributeFromSettings(settings, textItem, 'sweepAngle', 'number');
        applyAttributeFromSettings(settings, textItem, 'align');
        applyAttributeFromSettings(settings, textItem, 'textBaseline');
        applyAttributeFromSettings(settings, textItem, 'rotation', 'number');
        applyAttributeFromSettings(settings, textItem, 'x', 'float');
        applyAttributeFromSettings(settings, textItem, 'y', 'float');
        applyAttributeFromSettings(settings, textItem, 'data');
        applyAttributeFromSettings(settings, textItem, 'scaleX', 'number');
        applyAttributeFromSettings(settings, textItem, 'scaleY', 'number');
    });

    designVariantDetail.textItems = textItems;
}

const applyAttributeFromSettings = (settings, textItem, attribute, attributeType?) => {
    textItem[attribute] = settings[attribute];

    if (attributeType === 'number') {
        textItem[attribute] = _.parseInt(textItem[attribute]);

        if (_.isNaN(textItem[attribute])) {
            delete textItem[attribute];
        }
    }

    if (attributeType === 'float') {
        if (textItem[attribute]) {
            textItem[attribute] = parseFloat(textItem[attribute]);
        } else {
            textItem[attribute] = 0;
        }
    }

    if (textItem[attribute] === undefined) {
        delete textItem[attribute];
    }
}

const extractColorsFromDesignVariant = (designVariant) => {
    const colors = [];

    const settings = designVariant?.settings;
    const textItems = designVariant?.textItems;
    const background = settings?.background;

    let colorKeys = [];

    if (!settings?.noText) {
        _.forEach(textItems, textItem => {
            colorKeys.push(textItem.colorKey);
            colorKeys.push(textItem.strokeColorKey);
            colorKeys.push(textItem.shadowColorKey);
        });
    }

    if (background) {
        if (background?.colorType === 'solid') {
            colorKeys.push(background?.backgroundColorKey);
        } else {
            _.forEach(background?.gradientColorList, gradientColor => {
                colorKeys.push(gradientColor.backgroundColorKey);
            });
        }
    }

    colorKeys = _.compact(_.uniq(colorKeys));
    const colorsMap = _.keyBy(COLOR_LIST, 'colorKey');

    _.forEach(colorKeys, colorKey => {
        let colorDetail = colorsMap[colorKey];

        if (colorDetail) {
            colors.push({
                startText: colorDetail.colorName,
                colorKey: colorDetail.colorKey,
            })
        }
    });

    return colors;
}

const prepareDesignVariantLabel = (designVariant, designDetail) => {
    designVariant.name = designDetail.name;
    designVariant.variantIndex = _.parseInt(_.replace(designVariant.key, DESIGN_KEY_PREFIX.VARIANT + '-', ''));
    designVariant.label = designVariant.name + ' - #' + designVariant.variantIndex;
}
